// Accounting › Sales tax (Blueprint's; pages/ux-full accounting/tax), its own module out of Books.tsx
// (BL-189): PageHead, the month (last month or this month), then one return for each tax the company
// charges (tax.listZones' zone its invoices use), side by side, each worked out from the posted lines
// (tax.computeReturn). A return is a Section, its name and where it stands (StateMark and its word), its
// figures a KeyValue's rows, then its form: File the return, or Pay from, Amount and Record the payment.
// Filing locks the figures; the payment posts out of the bank (tax.recordRemittance). Riverstone keeps
// books/Tax.tsx. Keeps its own skeleton (patterns/README.md: the books' tax); tax-filing-page.css lays
// out the returns and the form, tokens only.
import {
  Button,
  KeyValue,
  Link,
  Segmented,
  SelectField,
  Skeleton,
  SkeletonGroup,
  TextField,
  type KeyValueItem,
  type MarkState,
} from '@flowology/ui-web/fl';
import { PageHead, Section } from '@flowology/ui-web/patterns';
import { useState } from 'react';
import { PageBanners, usePageCrumb, type NavProps } from '../Nav.tsx';
import { newId } from '../shared/ids.ts';
import { money, monthName, monthOf, parseMoney, previousMonth, todayIn } from './model.ts';
import { BOOKS_PARTS } from './parts.ts';
import { useSalesTaxCodes } from './tax-codes.ts';
import type { BankAccount, TaxReturn as Return } from './types.ts';
import { useAct, useQuery } from './ui.tsx';
import './tax-filing-page.css';

const PART = BOOKS_PARTS.find((p) => p.part === 'tax')!;
const NO_TAX =
  'No sales tax is set up yet, so there are no returns. The owner sets it up under Company → Getting started.';

/** A red sentence and Try again: a return, or the month's returns, that didn't load. */
function Failed({ text, onRetry }: { text: string; onRetry: () => void }) {
  return (
    <p className="bp-taxp-bad" role="alert">
      <span>{text}</span>
      <Link onPress={onRetry}>Try again</Link>
    </p>
  );
}

/** Where a return stands, the word beside its name: Not filed waits; Filed and Nothing to file are settled. */
function standing(entries: number, filed: boolean): { mark: MarkState; word: string } {
  if (filed) return { mark: 'done', word: 'Filed' };
  if (entries === 0) return { mark: 'done', word: 'Nothing to file' };
  return { mark: 'waiting', word: 'Not filed' };
}

function OneReturn({
  code,
  start,
  end,
  banks,
  toast,
}: {
  code: string;
  start: string;
  end: string;
  banks: BankAccount[];
  toast: (m: string) => void;
}) {
  const r = useQuery<Return>('tax.computeReturn', { taxCode: code, periodStart: start, periodEnd: end });
  const act = useAct(toast, () => r.reload());
  const [confirmation, setConfirmation] = useState('');
  const [amount, setAmount] = useState('');
  const [bank, setBank] = useState(banks[0]?.id ?? '');
  const title = `${code} · ${monthName(start)}`;
  if (!r.data)
    return (
      <Section title={title}>
        {r.error ? (
          <Failed text={r.error} onRetry={() => void r.reload()} />
        ) : (
          <SkeletonGroup label={`Loading ${title}`}>
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} shape="row" width="full" />
            ))}
          </SkeletonGroup>
        )}
      </Section>
    );
  const { figures: f, filed } = r.data;
  const owing = filed ? f.netCents - filed.remittedCents : f.netCents;
  const rows: KeyValueItem[] = [
    { label: 'Taxable sales', value: money(f.salesCents) },
    { label: 'Collected', value: money(f.collectedCents) },
    // a tax with no credits on purchases (PST) has no Input tax credits row
    ...(code === 'PST' ? [] : [{ label: 'Input tax credits', value: money(-f.itcCents) }]),
    { label: 'Net owing', value: money(f.netCents), tone: 'strong' },
    ...(f.roundingCents !== 0
      ? [{ label: 'Rounding on the lines', value: money(f.roundingCents), tone: 'muted' as const }]
      : []),
  ];
  return (
    <Section title={title} state={standing(f.entryCount, !!filed)}>
      <div className="bp-taxp-return">
        <KeyValue layout="rows" label={`${title}’s figures`} items={rows} />
        <p className="bp-taxp-sub">
          From {f.entryCount} posted entr{f.entryCount === 1 ? 'y' : 'ies'}.
        </p>
        {!filed ? (
          <div className="bp-taxp-form">
            <TextField
              label="Confirmation number"
              value={confirmation}
              onChange={setConfirmation}
              placeholder="From the filing site (optional)"
            />
            <div>
              <Button
                variant="primary"
                isDisabled={act.busy}
                onPress={() =>
                  void act.run(
                    'tax.fileReturn',
                    {
                      taxCode: code,
                      periodStart: start,
                      periodEnd: end,
                      ...(confirmation ? { confirmation } : {}),
                    },
                    `${code} for ${monthName(start)} filed.`,
                  )
                }
              >
                File the return
              </Button>
            </div>
          </div>
        ) : owing > 0 ? (
          <div className="bp-taxp-form">
            <SelectField
              label="Pay from"
              value={bank}
              onChange={setBank}
              options={banks.map((b) => ({ value: b.id, label: b.name }))}
            />
            <TextField
              label="Amount"
              inputMode="decimal"
              value={amount}
              onChange={setAmount}
              placeholder={(owing / 100).toFixed(2)}
            />
            <div>
              <Button
                variant="primary"
                isDisabled={act.busy || !bank}
                onPress={() =>
                  void act.run(
                    'tax.recordRemittance',
                    {
                      remittanceId: newId(),
                      returnId: filed.id,
                      amountCents: parseMoney(amount) ?? owing,
                      paidOn: todayIn(),
                      bankAccountId: bank,
                    },
                    'Remittance posted.',
                  )
                }
              >
                Record the payment
              </Button>
            </div>
          </div>
        ) : (
          <p className="bp-taxp-done">Filed and paid.</p>
        )}
      </div>
    </Section>
  );
}

export function TaxFilingPage({ nav, toast }: { nav: NavProps; toast: (m: string) => void }) {
  const codes = useSalesTaxCodes();
  const today = todayIn();
  const months = [previousMonth(today), monthOf(today)];
  const [m, setM] = useState(0);
  const banks = useQuery<{ bankAccounts: BankAccount[] }>('bankAccounts.list', {});
  const [start, end] = months[m]!;
  const crumb = usePageCrumb(nav);
  return (
    <div className="app">
      <main>
        <PageBanners nav={nav} />
        <PageHead {...(crumb ? { crumb } : {})} title={PART.bp ?? PART.rail} lede={PART.lede} />
        <div className="bp-taxp">
          <div className="bp-taxp-months">
            <Segmented
              label="Month"
              items={months.map(([s]) => ({ id: s, label: monthName(s) }))}
              selectedKey={start}
              onSelectionChange={(id) =>
                setM(
                  Math.max(
                    0,
                    months.findIndex(([s]) => s === id),
                  ),
                )
              }
            />
          </div>
          {banks.error ? (
            <Failed text={banks.error} onRetry={() => void banks.reload()} />
          ) : !banks.data || !codes ? (
            <SkeletonGroup label="Loading the returns">
              {Array.from({ length: 3 }, (_, i) => (
                <Skeleton key={i} shape="row" width="full" />
              ))}
            </SkeletonGroup>
          ) : !codes.length ? (
            <p className="bp-taxp-empty">{NO_TAX}</p>
          ) : (
            <div className="bp-taxp-returns">
              {codes.map((c) => (
                <OneReturn
                  key={`${c}${start}`}
                  code={c}
                  start={start}
                  end={end}
                  banks={banks.data!.bankAccounts}
                  toast={toast}
                />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
